export const CALENDAR_ICS_LIMITS = Object.freeze({
  maxAttendeesPerEvent: 500,
  maxBytes: 1024 * 1024,
  maxComponentDepth: 16,
  maxEvents: 1000,
  maxExpandedDaysPerEvent: 3660,
  maxMailAggregateBytes: 4 * 1024 * 1024,
  maxMailCalendarPayloads: 8,
  maxPhysicalLines: 50_000,
  maxProperties: 50_000,
  maxUnfoldedLineBytes: 64 * 1024,
  maxUrlCharacters: 2048,
});

export type CalendarIcsLimits = {
  [Key in keyof typeof CALENDAR_ICS_LIMITS]: number;
};

export type CalendarIcsSecurityErrorCode =
  | 'control_character'
  | 'invalid_calendar'
  | 'limit_attendees'
  | 'limit_bytes'
  | 'limit_component_depth'
  | 'limit_events'
  | 'limit_line_bytes'
  | 'limit_lines'
  | 'limit_properties'
  | 'unsafe_url';

export class CalendarIcsSecurityError extends Error {
  constructor(
    readonly code: CalendarIcsSecurityErrorCode,
    message: string
  ) {
    super(message);
    this.name = 'CalendarIcsSecurityError';
  }
}

export type CalendarIcsPreflight = {
  bytes: number;
  events: number;
  lines: number;
  properties: number;
};

export function calendarIcsByteLength(value: string): number {
  return new TextEncoder().encode(value).byteLength;
}

export function assertCalendarIcsBytes(
  byteLength: number,
  maxBytes = CALENDAR_ICS_LIMITS.maxBytes
): void {
  if (!Number.isSafeInteger(byteLength) || byteLength < 0) {
    throw new CalendarIcsSecurityError(
      'limit_bytes',
      'Calendar file size is invalid.'
    );
  }
  if (byteLength > maxBytes) {
    throw new CalendarIcsSecurityError(
      'limit_bytes',
      `Calendar file exceeds the ${formatByteLimit(maxBytes)} safety limit.`
    );
  }
}

export function assertCalendarIcsText(
  text: string,
  overrides: Partial<CalendarIcsLimits> = {}
): CalendarIcsPreflight {
  const limits = { ...CALENDAR_ICS_LIMITS, ...overrides };
  const bytes = calendarIcsByteLength(text);
  assertCalendarIcsBytes(bytes, limits.maxBytes);
  if (/\u0000|[\u0001-\u0008\u000b\u000c\u000e-\u001f\u007f]/u.test(text)) {
    throw new CalendarIcsSecurityError(
      'control_character',
      'Calendar file contains unsupported control characters.'
    );
  }

  const physicalLines = text.split(/\r\n|\n|\r/);
  if (physicalLines.length > limits.maxPhysicalLines) {
    throw new CalendarIcsSecurityError(
      'limit_lines',
      'Calendar file contains too many lines.'
    );
  }

  const unfolded: string[] = [];
  for (const line of physicalLines) {
    if (/^[ \t]/.test(line) && unfolded.length > 0) {
      unfolded[unfolded.length - 1] += line.slice(1);
    } else {
      unfolded.push(line);
    }
    const current = unfolded[unfolded.length - 1] ?? '';
    if (calendarIcsByteLength(current) > limits.maxUnfoldedLineBytes) {
      throw new CalendarIcsSecurityError(
        'limit_line_bytes',
        'Calendar file contains an oversized property.'
      );
    }
  }

  const stack: string[] = [];
  let attendeesInEvent = 0;
  let events = 0;
  let finishedCalendar = false;
  let properties = 0;
  let sawCalendar = false;
  for (const line of unfolded) {
    if (!line) continue;
    if (finishedCalendar) {
      throw new CalendarIcsSecurityError(
        'invalid_calendar',
        'Calendar file contains data outside its calendar root.'
      );
    }
    const separator = line.indexOf(':');
    if (separator <= 0) {
      throw new CalendarIcsSecurityError(
        'invalid_calendar',
        'Calendar file contains a malformed property.'
      );
    }
    const rawName = line.slice(0, separator);
    const name = rawName.split(';', 1)[0]?.trim().toUpperCase() ?? '';
    const value = line
      .slice(separator + 1)
      .trim()
      .toUpperCase();
    if (name === 'BEGIN') {
      const parent = stack.at(-1);
      if (value === 'VCALENDAR') {
        if (sawCalendar || stack.length !== 0) {
          throw new CalendarIcsSecurityError(
            'invalid_calendar',
            'Calendar file contains an invalid component hierarchy.'
          );
        }
        sawCalendar = true;
      } else if (stack.length === 0) {
        throw new CalendarIcsSecurityError(
          'invalid_calendar',
          'Calendar file must contain exactly one calendar root.'
        );
      }
      if (value === 'VEVENT' && parent !== 'VCALENDAR') {
        throw new CalendarIcsSecurityError(
          'invalid_calendar',
          'Calendar events must be direct children of the calendar root.'
        );
      }
      stack.push(value);
      if (stack.length > limits.maxComponentDepth) {
        throw new CalendarIcsSecurityError(
          'limit_component_depth',
          'Calendar file is nested too deeply.'
        );
      }
      if (value === 'VEVENT') {
        events += 1;
        attendeesInEvent = 0;
        if (events > limits.maxEvents) {
          throw new CalendarIcsSecurityError(
            'limit_events',
            'Calendar file contains too many events.'
          );
        }
      }
      continue;
    }
    if (name === 'END') {
      if (stack.pop() !== value) {
        throw new CalendarIcsSecurityError(
          'invalid_calendar',
          'Calendar file has mismatched components.'
        );
      }
      if (value === 'VCALENDAR') finishedCalendar = true;
      continue;
    }
    if (stack.length === 0) {
      throw new CalendarIcsSecurityError(
        'invalid_calendar',
        'Calendar file contains data outside its calendar root.'
      );
    }
    properties += 1;
    if (properties > limits.maxProperties) {
      throw new CalendarIcsSecurityError(
        'limit_properties',
        'Calendar file contains too many properties.'
      );
    }
    if (name === 'ATTENDEE' && stack.at(-1) === 'VEVENT') {
      attendeesInEvent += 1;
      if (attendeesInEvent > limits.maxAttendeesPerEvent) {
        throw new CalendarIcsSecurityError(
          'limit_attendees',
          'Calendar event contains too many attendees.'
        );
      }
    }
  }
  if (!sawCalendar || stack.length !== 0) {
    throw new CalendarIcsSecurityError(
      'invalid_calendar',
      'Calendar file is incomplete or invalid.'
    );
  }
  return { bytes, events, lines: physicalLines.length, properties };
}

export function decodeCalendarIcsBytes(
  value: Uint8Array,
  overrides: Partial<CalendarIcsLimits> = {}
): string {
  const limits = { ...CALENDAR_ICS_LIMITS, ...overrides };
  assertCalendarIcsBytes(value.byteLength, limits.maxBytes);
  let text: string;
  try {
    text = new TextDecoder('utf-8', { fatal: true }).decode(value);
  } catch {
    throw new CalendarIcsSecurityError(
      'invalid_calendar',
      'Calendar file is not valid UTF-8.'
    );
  }
  assertCalendarIcsText(text, limits);
  return text;
}

type CalendarIcsResponse = {
  body?: {
    getReader(): {
      cancel(reason?: unknown): Promise<void>;
      read(): Promise<{ done: boolean; value?: Uint8Array }>;
      releaseLock(): void;
    };
  } | null;
  headers?: { get(name: string): string | null };
  text(): Promise<string>;
};

export async function readBoundedCalendarIcsResponse(
  response: CalendarIcsResponse,
  maxBytes = CALENDAR_ICS_LIMITS.maxBytes
): Promise<string> {
  const contentLength = response.headers?.get('content-length');
  if (contentLength) {
    const declared = Number(contentLength);
    if (Number.isFinite(declared)) assertCalendarIcsBytes(declared, maxBytes);
  }
  if (!response.body) {
    const text = await response.text();
    assertCalendarIcsText(text, { maxBytes });
    return text;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8', { fatal: true });
  const parts: string[] = [];
  let total = 0;
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      if (!value) continue;
      total += value.byteLength;
      if (total > maxBytes) {
        await reader.cancel('Calendar file exceeds safety limit');
        assertCalendarIcsBytes(total, maxBytes);
      }
      parts.push(decoder.decode(value, { stream: true }));
    }
    parts.push(decoder.decode());
  } catch (error) {
    if (error instanceof CalendarIcsSecurityError) throw error;
    throw new CalendarIcsSecurityError(
      'invalid_calendar',
      'Calendar file is not valid UTF-8.'
    );
  } finally {
    reader.releaseLock();
  }
  const text = parts.join('');
  assertCalendarIcsText(text, { maxBytes });
  return text;
}

export function safeCalendarExternalUrl(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed || trimmed.length > CALENDAR_ICS_LIMITS.maxUrlCharacters) {
    return null;
  }
  try {
    const parsed = new URL(trimmed);
    if (
      (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') ||
      parsed.username ||
      parsed.password
    ) {
      return null;
    }
    return parsed.toString();
  } catch {
    return null;
  }
}

export function assertSafeCalendarExternalUrl(value: string): string {
  const safe = safeCalendarExternalUrl(value);
  if (!safe) {
    throw new CalendarIcsSecurityError(
      'unsafe_url',
      'Calendar URL must use http or https and must not contain credentials.'
    );
  }
  return safe;
}

function formatByteLimit(bytes: number): string {
  return bytes % (1024 * 1024) === 0
    ? `${bytes / (1024 * 1024)} MiB`
    : `${bytes} byte`;
}
